import { useState, useEffect, createContext, useContext, useCallback, ReactNode } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

// Toast types
export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  variant: ToastVariant;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  success: (title: string, message?: string) => string;
  error: (title: string, message?: string) => string;
  warning: (title: string, message?: string) => string;
  info: (title: string, message?: string) => string;
  showToast: (options: { type: ToastVariant; title: string; message?: string }) => string;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// Generate unique IDs
let toastId = 0;
const generateId = () => `toast-${++toastId}-${Date.now()}`;

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = generateId();
    setToasts((prev) => [...prev, { ...toast, id }]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const success = useCallback(
    (title: string, message?: string) => {
      return addToast({ variant: 'success', title, message, duration: 4000 });
    },
    [addToast]
  );

  const error = useCallback(
    (title: string, message?: string) => {
      return addToast({ variant: 'error', title, message, duration: 6000 });
    },
    [addToast]
  );

  const warning = useCallback(
    (title: string, message?: string) => {
      return addToast({ variant: 'warning', title, message, duration: 5000 });
    },
    [addToast]
  );

  const info = useCallback(
    (title: string, message?: string) => {
      return addToast({ variant: 'info', title, message, duration: 4000 });
    },
    [addToast]
  );

  const showToast = useCallback(
    (options: { type: ToastVariant; title: string; message?: string }) => {
      const durations: Record<ToastVariant, number> = {
        success: 4000,
        error: 6000,
        warning: 5000,
        info: 4000,
      };
      return addToast({
        variant: options.type,
        title: options.title,
        message: options.message,
        duration: durations[options.type],
      });
    },
    [addToast]
  );

  return (
    <ToastContext.Provider
      value={{ toasts, addToast, removeToast, success, error, warning, info, showToast }}
    >
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
}

// Icon and color configuration
const toastConfig: Record<
  ToastVariant,
  {
    icon: typeof CheckCircle;
    iconClassName: string;
    bgClassName: string;
    borderClassName: string;
  }
> = {
  success: {
    icon: CheckCircle,
    iconClassName: 'text-green-500',
    bgClassName: 'bg-green-50 dark:bg-green-900/20',
    borderClassName: 'border-green-200 dark:border-green-800',
  },
  error: {
    icon: XCircle,
    iconClassName: 'text-red-500',
    bgClassName: 'bg-red-50 dark:bg-red-900/20',
    borderClassName: 'border-red-200 dark:border-red-800',
  },
  warning: {
    icon: AlertTriangle,
    iconClassName: 'text-amber-500',
    bgClassName: 'bg-amber-50 dark:bg-amber-900/20',
    borderClassName: 'border-amber-200 dark:border-amber-800',
  },
  info: {
    icon: Info,
    iconClassName: 'text-blue-500',
    bgClassName: 'bg-blue-50 dark:bg-blue-900/20',
    borderClassName: 'border-blue-200 dark:border-blue-800',
  },
};

interface ToastContainerProps {
  toasts: Toast[];
  removeToast: (id: string) => void;
}

function ToastContainer({ toasts, removeToast }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 z-200 flex flex-col gap-3 pointer-events-none"
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={() => removeToast(toast.id)} />
      ))}
    </div>
  );
}

interface ToastItemProps {
  toast: Toast;
  onDismiss: () => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const config = toastConfig[toast.variant];
  const Icon = config.icon;

  // Animate in on mount
  useEffect(() => {
    // Small delay to trigger the transition
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  // Auto-dismiss after duration
  useEffect(() => {
    const duration = toast.duration || 4000;
    const timer = setTimeout(() => {
      handleDismiss();
    }, duration);
    return () => clearTimeout(timer);
  }, [toast.duration]);

  const handleDismiss = () => {
    setIsExiting(true);
    // Wait for exit animation to complete
    setTimeout(onDismiss, 200);
  };

  return (
    <div
      className={`
        pointer-events-auto
        w-80 max-w-[calc(100vw-2rem)]
        rounded-lg border shadow-lg
        ${config.bgClassName}
        ${config.borderClassName}
        transform transition-all duration-200 ease-out
        ${isVisible && !isExiting ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
      role="alert"
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="shrink-0">
            <Icon className={`w-5 h-5 ${config.iconClassName}`} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 dark:text-white">
              {toast.title}
            </p>
            {toast.message && (
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                {toast.message}
              </p>
            )}
          </div>

          {/* Dismiss button */}
          <button
            onClick={handleDismiss}
            className="shrink-0 p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Progress bar for auto-dismiss */}
      <div className="h-1 bg-slate-200/50 dark:bg-slate-700/50 rounded-b-lg overflow-hidden">
        <div
          className={`h-full ${
            toast.variant === 'success'
              ? 'bg-green-500'
              : toast.variant === 'error'
              ? 'bg-red-500'
              : toast.variant === 'warning'
              ? 'bg-amber-500'
              : 'bg-blue-500'
          }`}
          style={{
            animation: `shrink ${toast.duration || 4000}ms linear forwards`,
          }}
        />
      </div>

      {/* Keyframes for progress bar */}
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}

export default ToastProvider;
